// Cloudflare Turnstile bootstrap for free-tools pages.
//
// Behavior is driven by data attributes on <body>:
//   data-turnstile-enabled="true|false"
//   data-turnstile-site-key="..." (when enabled)
//
// When disabled (development / test): immediately resolves to "passed" so
// the rest of the page can wire up tool interactions with no friction.
//
// When enabled: loads the Turnstile script on demand, executes the challenge
// in invisible/managed mode, POSTs the token to /free-tools/verify, and only
// then resolves "passed". On failure resolves "failed" with a friendly
// message — the page itself stays rendered, only interactive controls are
// disabled by the caller.
//
// Tokens stay in memory only. The server sets a short-lived signed cookie so
// repeated processing within the same session doesn't re-challenge.

import { trackTurnstile } from "./analytics.js"

const TURNSTILE_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=__postcrestTurnstileLoaded"
const VERIFY_URL = "/free-tools/verify"

let scriptLoadingPromise = null
let widgetId = null
let currentTokenPromise = null

function turnstileEnabled() {
  return document.body?.dataset?.turnstileEnabled === "true"
}

function turnstileSiteKey() {
  return document.body?.dataset?.turnstileSiteKey || ""
}

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || ""
}

function loadTurnstileScript() {
  if (scriptLoadingPromise) return scriptLoadingPromise
  scriptLoadingPromise = new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve()
      return
    }
    window.__postcrestTurnstileLoaded = () => resolve()
    const s = document.createElement("script")
    s.src = TURNSTILE_SRC
    s.async = true
    s.defer = true
    s.onerror = () => reject(new Error("turnstile_script_failed"))
    document.head.appendChild(s)
  })
  return scriptLoadingPromise
}

function executeTurnstile() {
  if (currentTokenPromise) return currentTokenPromise
  currentTokenPromise = new Promise((resolve, reject) => {
    const host = document.createElement("div")
    host.setAttribute("aria-hidden", "true")
    host.style.cssText = "position:absolute;left:-10000px;top:-10000px;width:1px;height:1px;overflow:hidden;"
    document.body.appendChild(host)

    try {
      // No size: "invisible" — Cloudflare removed that value. Invisibility is
      // now controlled by the widget type set on the sitekey in the Cloudflare
      // dashboard (use a "Managed" or "Invisible" widget). The off-screen host
      // div keeps it visually hidden regardless.
      widgetId = window.turnstile.render(host, {
        sitekey: turnstileSiteKey(),
        retry: "auto",
        callback: (token) => resolve(token),
        "error-callback": () => reject(new Error("turnstile_error")),
        "expired-callback": () => {
          // silently re-execute and replace stored token
          currentTokenPromise = null
        }
      })
    } catch (e) {
      reject(e)
    }
  })
  return currentTokenPromise
}

async function verifyOnServer(token) {
  const body = token ? new URLSearchParams({ token }) : new URLSearchParams()
  const res = await fetch(VERIFY_URL, {
    method: "POST",
    headers: {
      "X-CSRF-Token": csrfToken(),
      "Accept": "application/json",
      "Content-Type": "application/x-www-form-urlencoded"
    },
    credentials: "same-origin",
    body
  })
  if (!res.ok) throw new Error("verify_failed")
  return await res.json()
}

// Resolves once the visitor has passed Turnstile (or immediately when disabled).
// Caller can call this multiple times during a session — it dedupes.
let primedPromise = null

export function prime({ toolSlug } = {}) {
  if (primedPromise) return primedPromise

  primedPromise = (async () => {
    if (!turnstileEnabled()) {
      // Dev/test: server short-circuits. Get the session marker but don't load script.
      try {
        await verifyOnServer(null)
      } catch (e) {
        // Even if the verify call fails in dev (server down?), let tools run
      }
      return { passed: true }
    }

    try {
      await loadTurnstileScript()
      const token = await executeTurnstile()
      await verifyOnServer(token)
      trackTurnstile({ toolSlug, outcome: "passed" })
      return { passed: true }
    } catch (e) {
      trackTurnstile({ toolSlug, outcome: "failed" })
      return { passed: false, error: "challenge_failed" }
    }
  })()

  return primedPromise
}

export function reset() {
  primedPromise = null
  currentTokenPromise = null
  if (widgetId && window.turnstile) {
    try { window.turnstile.reset(widgetId) } catch (e) {}
  }
}
